import { Fragment, Slice, type Node as PMNode, type ResolvedPos, type Schema } from 'prosemirror-model'

const LISTS = new Set(['bulletList', 'orderedList', 'taskList'])
const LIST_MARKER = /^\s*(?:[-*•]|\d+[.)])\s+/

function isInTaskItem($pos: ResolvedPos): boolean {
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === 'taskItem') return true
  }
  return false
}

function withoutMarker(paragraph: PMNode): PMNode {
  const first = paragraph.firstChild
  if (!first?.isText || !LIST_MARKER.test(first.text!)) return paragraph
  const rest = first.text!.replace(LIST_MARKER, '')
  const tail = paragraph.content.cut(first.nodeSize)
  return paragraph.copy(rest ? tail.addToStart(paragraph.type.schema.text(rest, first.marks)) : tail)
}

/** One task item per list item; a nested list inside an item becomes a nested task list. */
function listToTaskItems(list: PMNode, schema: Schema): PMNode[] {
  const { taskItem, taskList } = schema.nodes
  const items: PMNode[] = []
  list.forEach(li => {
    if (li.type === taskItem) {
      items.push(li)
      return
    }
    const children: PMNode[] = []
    li.forEach(child => {
      if (LISTS.has(child.type.name)) {
        const nested = listToTaskItems(child, schema)
        if (nested.length) children.push(taskList.create(null, nested))
      } else {
        children.push(child)
      }
    })
    if (children.length) items.push(taskItem.create({ checked: false }, children))
  })
  return items
}

/**
 * Pasting several plain lines (or an ordered/bullet list) into a checklist item would stack them as paragraphs or
 * nest a plain list in that one item. Turns them into sibling task items instead; any other slice comes back as the
 * same object.
 */
export function paragraphsToTaskItems(slice: Slice, $from: ResolvedPos): Slice {
  const { content } = slice
  const { schema } = $from.doc.type
  const { taskItem, taskList } = schema.nodes
  if (!taskItem || !taskList || !isInTaskItem($from)) return slice
  let hasList = false
  let supported = true
  content.forEach(n => {
    if (LISTS.has(n.type.name)) hasList = true
    else if (n.type.name !== 'paragraph') supported = false
  })
  if (!supported || (!hasList && content.childCount < 2)) return slice

  const items: PMNode[] = []
  content.forEach(n => {
    if (LISTS.has(n.type.name)) {
      items.push(...listToTaskItems(n, schema))
      return
    }
    const line = withoutMarker(n)
    if (line.content.size > 0) items.push(taskItem.create({ checked: false }, line))
  })
  if (items.length < (hasList ? 1 : 2)) return slice
  return new Slice(Fragment.fromArray(items), 2, 2)
}
